import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import SuccessModal from './SuccessModal';

const API_BASE_URL = 'https://www.testometer.in:8084/api';

const EditProjectModal = ({ isOpen, onClose, onProjectUpdated, projectToEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    responsibilities: '',
    technologies: '',
    category: '',
    difficulty: '',
    client: '',
    domain: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Update form data when projectToEdit changes
  useEffect(() => {
    if (projectToEdit) {
      setFormData({
        name: projectToEdit.name || '',
        description: projectToEdit.description || '',
        responsibilities: projectToEdit.responsibilities || '',
        technologies: projectToEdit.technologies || '',
        category: projectToEdit.category || '',
        difficulty: projectToEdit.difficulty || '',
        client: projectToEdit.client || '',
        domain: projectToEdit.domain || ''
      });
    }
  }, [projectToEdit]);

  useEffect(() => {
    let timer;
    if (showSuccessModal) {
      timer = setTimeout(() => setShowSuccessModal(false), 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await axios.put(`${API_BASE_URL}/master-data/projects/${projectToEdit.id}`, formData);
      
      // Notify parent component
      onProjectUpdated(response.data);
      
      // Close modal
      onClose();
      setShowSuccessModal(true);
      setSuccessMessage('Project updated successfully!');
    } catch (error) {
      console.error('Error updating project:', error);
      setError('Error updating project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  if (!isOpen || !projectToEdit) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Project</h2>
          <button 
            onClick={handleClose} 
            className="modal-close"
            disabled={loading}
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Project Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="input-field"
                placeholder="Enter project name"
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="input-field"
                disabled={loading}
              >
                <option value="">Select Category</option>
                <option value="Web Development">Web Development</option>
                <option value="Mobile App">Mobile App</option>
                <option value="Data Science">Data Science</option>
                <option value="System">System</option>
                <option value="API Development">API Development</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Difficulty</label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                className="input-field"
                disabled={loading}
              >
                <option value="">Select Difficulty</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Technologies</label>
              <input
                type="text"
                value={formData.technologies}
                onChange={(e) => setFormData({...formData, technologies: e.target.value})}
                className="input-field"
                placeholder="e.g., React, Node.js, MongoDB"
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Description</label>
            <ReactQuill
              value={formData.description}
              onChange={value => setFormData({...formData, description: value})}
              className="input-field quill-field"
              placeholder="Describe the project, its purpose, and key features..."
              theme="snow"
              readOnly={loading}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Responsibilities</label>
            <ReactQuill
              value={formData.responsibilities}
              onChange={value => setFormData({...formData, responsibilities: value})}
              className="input-field quill-field"
              placeholder="Describe your specific responsibilities and contributions to this project..."
              theme="snow"
              readOnly={loading}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Client</label>
            <input
              type="text"
              value={formData.client}
              onChange={(e) => setFormData({...formData, client: e.target.value})}
              className="input-field"
              placeholder="Enter client name (if any)"
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Project Domain</label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData({...formData, domain: e.target.value})}
              className="input-field"
              placeholder="Enter project domain (e.g., Finance, Healthcare)"
              disabled={loading}
            />
          </div>
          
          <div className="modal-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Project'}
            </button>
          </div>
        </form>
      </div>
      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} message={successMessage} />
    </div>
  );
};

export default EditProjectModal; 